import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import {
  User,
  Lock,
  Shield,
  Smartphone,
  Key,
  Loader2,
  CheckCircle,
  Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getApiErrorMessage } from '../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const { user, updateProfile, changePassword, setup2FA, enable2FA, disable2FA } = useAuth();
  const [activeTab, setActiveTab] = useState(() => (tabFromUrl === '2fa' || tabFromUrl === 'security' ? tabFromUrl : 'profile'));
  const [loading, setLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || '' });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAData, setTwoFAData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name });
    }
  }, [user]);

  useEffect(() => {
    if (tabFromUrl === '2fa' || tabFromUrl === 'security') setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profileForm.name);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const data = await setup2FA();
      setTwoFAData(data);
      setShow2FASetup(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await enable2FA(verifyCode);
      toast.success('2FA enabled successfully');
      setShow2FASetup(false);
      setVerifyCode('');
      setTwoFAData(null);
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await disable2FA(disableCode);
      toast.success('2FA disabled');
      setDisableCode('');
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Layout>
      <div data-testid="settings-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-extrabold text-[#0F172A] ">
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Manage your account preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger
              value="profile"
              className="rounded-lg text-slate-600 data-[state=active]:bg-[#0B6445] data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className="rounded-lg text-slate-600 data-[state=active]:bg-[#0B6445] data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger
              value="2fa"
              className="rounded-lg text-slate-600 data-[state=active]:bg-[#0B6445] data-[state=active]:text-white data-[state=active]:shadow-sm"
            >
              <Shield className="w-4 h-4 mr-2" />
              Two-Factor Auth
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold ">
                  Profile Information
                </CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-6 max-w-md">
                  <div>
                    <Label className="input-label">Full Name</Label>
                    <Input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="input-field"
                      data-testid="profile-name"
                      autoComplete="name"
                    />
                  </div>

                  <div>
                    <Label className="input-label">Email Address</Label>
                    <Input
                      type="email"
                      value={user?.email || ''}
                      className="input-field bg-slate-50"
                      disabled
                      autoComplete="email"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Email cannot be changed
                    </p>
                  </div>

                  <div>
                    <Label className="input-label">Role</Label>
                    <Input
                      type="text"
                      value={user?.role || ''}
                      className="input-field bg-slate-50 capitalize"
                      disabled
                    />
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary hover:text-white"
                    disabled={loading}
                    data-testid="save-profile"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold ">
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-6 max-w-md">
                  {/* Hidden username for accessibility / password managers (see https://goo.gl/9p2vKq) */}
                  <input
                    type="email"
                    name="username"
                    autoComplete="username"
                    value={user?.email ?? ''}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    className="absolute -left-[9999px] w-0 h-0 overflow-hidden"
                  />
                  <div>
                    <Label className="input-label">Current Password</Label>
                    <Input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                      }
                      className="input-field"
                      required
                      data-testid="current-password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div>
                    <Label className="input-label">New Password</Label>
                    <Input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      }
                      className="input-field"
                      required
                      data-testid="new-password"
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Minimum 8 characters
                    </p>
                  </div>

                  <div>
                    <Label className="input-label">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                      }
                      className="input-field"
                      required
                      data-testid="confirm-password"
                      autoComplete="new-password"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary hover:text-white"
                    disabled={loading}
                    data-testid="change-password"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* 2FA Tab */}
          <TabsContent value="2fa">
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold ">
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  {user?.is_2fa_enabled ? (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-[#E6F7F3] rounded-xl">
                        <div className="w-12 h-12 bg-[#1C472B] rounded-xl flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#1C472B]">2FA Enabled</p>
                          <p className="text-sm text-slate-600">
                            Your account is protected with two-factor authentication
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Label className="input-label">Disable 2FA</Label>
                        <p className="text-sm text-slate-500">
                          Enter your authenticator code to disable 2FA
                        </p>
                        <div className="flex gap-3">
                          <Input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={disableCode}
                            onChange={(e) =>
                              setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            placeholder="000000"
                            className="input-field flex-1 font-mono"
                            maxLength={6}
                            data-testid="disable-2fa-code"
                          />
                          <Button
                            type="button"
                            onClick={handleDisable2FA}
                            className="rounded-xl px-6 py-2.5 h-12 font-semibold bg-[#F08080] text-white border-0 shadow-sm transition-all cursor-pointer hover:bg-[#E07070] hover:text-white active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F08080]"
                            disabled={loading || disableCode.length !== 6}
                            data-testid="disable-2fa"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                        <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                          <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-800">2FA Not Enabled</p>
                          <p className="text-sm text-amber-700">
                            Enable two-factor authentication for enhanced security
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={handleSetup2FA}
                        type="button"
                        disabled={loading}
                        data-testid="setup-2fa"
                        className="min-h-[48px] px-6 py-3 rounded-xl font-semibold bg-[#0B6445] text-white border-0 shadow-md hover:bg-[#095b3d] hover:!text-white active:scale-[0.98] disabled:opacity-50 transition-colors"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Smartphone className="w-5 h-5 mr-2" />
                        )}
                        <span className="text-white">Setup 2FA</span>
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* 2FA Setup Dialog */}
        <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold ">
                Setup Two-Factor Authentication
              </DialogTitle>
              <DialogDescription>
                Scan the QR code with your authenticator app
              </DialogDescription>
            </DialogHeader>

            {twoFAData && (
              <div className="space-y-6 mt-4">
                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="p-4 bg-white border rounded-xl">
                    <img src={twoFAData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                </div>

                {/* Manual Entry */}
                <div>
                  <Label className="input-label">Manual Entry Code</Label>
                  <div className="flex gap-2">
                    <Input
                      value={twoFAData.secret}
                      readOnly
                      className="input-field font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => copyToClipboard(twoFAData.secret)}
                      className="border-[#DCE4E0] text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Backup Codes */}
                <div>
                  <Label className="input-label">Backup Codes</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Save these codes in a secure place
                  </p>
                  <div className="grid grid-cols-2 gap-2 p-4 bg-slate-50 rounded-xl">
                    {twoFAData.backup_codes.map((code, index) => (
                      <code key={index} className="text-sm font-mono text-slate-700">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>

                {/* Verify */}
                <div>
                  <Label className="input-label">Verify Code</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Enter the code from your authenticator app
                  </p>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="input-field flex-1 font-mono text-center text-lg"
                      maxLength={6}
                      data-testid="verify-2fa-code"
                    />
                    <Button
                      onClick={handleEnable2FA}
                      className="btn-primary hover:text-white"
                      disabled={loading || verifyCode.length !== 6}
                      data-testid="enable-2fa"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enable 2FA'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Settings;
